import { useState, useRef, useEffect, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CaretDown, Check, MagnifyingGlass, X } from '@phosphor-icons/react';

export const NexusSelect = ({
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Select an option',
  searchable = false,
  multiple = false,
  error,
  className = '',
  ...props
}) => {
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const filteredOptions = searchable
    ? options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;
  
  const selectedOptions = multiple
    ? options.filter(opt => value?.includes(opt.value))
    : options.find(opt => opt.value === value);
  
  const handleSelect = (option) => {
    if (multiple) {
      const newValue = value?.includes(option.value)
        ? value.filter(v => v !== option.value)
        : [...(value || []), option.value];
      onChange(newValue);
    } else {
      onChange(option.value);
      setIsOpen(false);
    }
    setSearchTerm('');
  };
  
  const removeTag = (optionValue, e) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== optionValue));
  };
  
  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label 
          htmlFor={id} 
          className={`
            block text-sm font-medium mb-2
            ${isOpen ? 'text-nexus-accent-mint' : 'text-nexus-text-secondary'}
            ${error ? 'text-nexus-error' : ''}
          `}
        >
          {label}
        </label>
      )}
      
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full bg-nexus-bg-primary/50 border text-nexus-text-primary
          px-4 py-2.5 rounded-sm cursor-pointer
          flex items-center justify-between gap-2
          transition-all duration-200
          ${isOpen && !error ? 'border-nexus-accent-mint ring-1 ring-nexus-accent-mint' : 'border-white/10'}
          ${error ? 'border-nexus-error ring-1 ring-nexus-error' : ''}
        `}
      >
        <div className="flex-1 flex flex-wrap gap-1">
          {multiple && selectedOptions?.length > 0 ? (
            selectedOptions.map(opt => (
              <span 
                key={opt.value}
                className="inline-flex items-center gap-1 bg-nexus-accent-mint/20 text-nexus-accent-mint text-xs px-2 py-0.5 rounded"
              >
                {opt.label}
                <X 
                  className="w-3 h-3 cursor-pointer hover:text-white" 
                  weight="bold"
                  onClick={(e) => removeTag(opt.value, e)}
                />
              </span>
            ))
          ) : selectedOptions ? (
            <span>{selectedOptions.label}</span>
          ) : (
            <span className="text-nexus-text-tertiary">{placeholder}</span>
          )}
        </div>
        <CaretDown 
          className={`w-5 h-5 text-nexus-text-tertiary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          weight="bold"
        />
      </div>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="absolute z-50 w-full mt-1 bg-nexus-bg-secondary border border-white/10 rounded-sm shadow-glass overflow-hidden"
          >
            {searchable && (
              <div className="p-2 border-b border-white/10">
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nexus-text-tertiary" weight="duotone" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className="w-full bg-nexus-bg-primary/50 border border-white/10 text-nexus-text-primary text-sm pl-9 pr-3 py-2 rounded focus:outline-none focus:border-nexus-accent-mint"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}
            
            <div className="max-h-60 overflow-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-3 text-sm text-nexus-text-tertiary text-center">
                  No options found
                </div>
              ) : (
                filteredOptions.map(option => {
                  const isSelected = multiple 
                    ? value?.includes(option.value)
                    : value === option.value;
                  
                  return (
                    <div
                      key={option.value}
                      onClick={() => handleSelect(option)}
                      className={`
                        px-4 py-2.5 text-sm cursor-pointer flex items-center justify-between
                        transition-colors duration-150
                        ${isSelected 
                          ? 'bg-nexus-accent-mint/10 text-nexus-accent-mint' 
                          : 'text-nexus-text-primary hover:bg-white/5'
                        }
                      `}
                    >
                      {option.label}
                      {isSelected && <Check className="w-4 h-4" weight="bold" />}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {error && (
        <p className="text-xs text-nexus-error mt-1.5">{error}</p>
      )}
    </div>
  );
};
